import { useEffect } from 'react';

interface UseRingtoneOptions {
  active: boolean;
  src: string;
}

/**
 * Loops a ringtone while `active` is true. Mirrors WhatsApp-style behavior:
 * the audio rings until the dialog is dismissed or the call is joined.
 *
 * Browser autoplay policy blocks `play()` until the page has received a user
 * gesture. A practitioner who loads the dashboard and just waits for calls has
 * given the page zero gestures, so the *first* ring was previously blocked and
 * silently swallowed — the dialog showed, but no sound played ("seen but not
 * heard"). To fix that we:
 *
 *  1. Prime + unlock audio on the first pointer/key gesture anywhere on the
 *     page (a muted play()/pause() satisfies the autoplay gate so later rings
 *     are allowed). This probe runs on its OWN dedicated element — never the
 *     ring element — so its muting/pausing can never silence a ring that is
 *     playing on the very same gesture.
 *  2. When `active` turns true, try to play immediately. If the browser still
 *     blocks it (no gesture has happened yet at all), arm a one-shot gesture
 *     listener so the ring starts the instant the user touches anything. That
 *     fallback plays the ring UNMUTED inside the gesture, which both sounds and
 *     unlocks the ring element — it does not depend on the probe above.
 */

let sharedAudio: HTMLAudioElement | null = null;
let unlockProbe: HTMLAudioElement | null = null;
let unlocked = false;

function getSharedAudio(src: string): HTMLAudioElement {
  if (!sharedAudio) {
    sharedAudio = new Audio(src);
    sharedAudio.loop = true;
    sharedAudio.preload = 'auto';
  } else if (sharedAudio.src === '' || !sharedAudio.src.endsWith(src)) {
    sharedAudio.src = src;
  }
  return sharedAudio;
}

/** Dedicated, non-looping element for the autoplay-unlock probe. Kept separate
 *  from the ring element so muting/pausing the probe can never touch a live
 *  ring. */
function getUnlockProbe(src: string): HTMLAudioElement {
  if (!unlockProbe) {
    unlockProbe = new Audio(src);
    unlockProbe.preload = 'auto';
  }
  return unlockProbe;
}

/**
 * Unlock audio on the first user gesture. A muted play()/pause() on the probe
 * element is enough for the browser to grant the page user-activation, so a
 * later ring is allowed even without a fresh gesture.
 *
 * Returns a cleanup that removes the still-armed listeners, so an unmount or a
 * StrictMode double-invoke can't leak duplicate unlock listeners.
 */
function installUnlockOnFirstGesture(src: string): () => void {
  if (typeof window === 'undefined' || unlocked) return () => undefined;

  const unlock = () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    const probe = getUnlockProbe(src);
    probe.muted = true;
    probe
      .play()
      .then(() => {
        probe.pause();
        probe.currentTime = 0;
        unlocked = true;
      })
      .catch(() => {
        // Still blocked (rare) — leave `unlocked` false so a later gesture retries.
      });
  };

  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
}

export function useRingtone({ active, src }: UseRingtoneOptions): void {
  // Arm the unlock listener as soon as the hook mounts, so the audio element is
  // user-activated well before the first call arrives.
  useEffect(() => {
    return installUnlockOnFirstGesture(src);
  }, [src]);

  useEffect(() => {
    if (!active) return;

    const audio = getSharedAudio(src);

    // Flips true the instant this ring is torn down (active → false, or
    // unmount). Everything below consults it so a late-resolving play()
    // rejection or a queued gesture can never resurrect a ring for a call that
    // is already over. This is what closes the "audible ring, no dialog" gap:
    // the gesture fallback used to be armed AFTER cleanup had run (see below)
    // and then replayed the loop=true element with no incoming call on screen.
    let cancelled = false;
    let gestureListener: (() => void) | null = null;

    const removeGestureListener = () => {
      if (!gestureListener) return;
      window.removeEventListener('pointerdown', gestureListener);
      window.removeEventListener('keydown', gestureListener);
      gestureListener = null;
    };

    // Autoplay was blocked (no user gesture yet). Arm a one-shot listener so the
    // ring fires the moment the user interacts — but only while this ring is
    // still active, and re-checking on fire so a call that ended in the gap
    // can't ring detached from the dialog.
    const armGestureFallback = () => {
      if (cancelled || gestureListener) return;
      const onGesture = () => {
        removeGestureListener();
        if (cancelled) return;
        void audio.play().catch(() => {
          /* give up silently — dialog still rings visually */
        });
      };
      gestureListener = onGesture;
      window.addEventListener('pointerdown', onGesture, { once: true });
      window.addEventListener('keydown', onGesture, { once: true });
    };

    audio.play().catch((err: unknown) => {
      // A pause() from this effect's own cleanup rejects the still-pending
      // play() with AbortError. That is teardown, not an autoplay block, so we
      // must NOT arm a resurrect listener for it — the rejection fires after
      // cleanup has already run, leaving a listener nothing would remove.
      if (cancelled) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      armGestureFallback();
    });

    return () => {
      cancelled = true;
      audio.pause();
      audio.currentTime = 0;
      removeGestureListener();
    };
  }, [active, src]);
}
